import { create } from 'zustand';

export interface NavEntry {
  label: string;
  path: string;
  filters?: string | null;
  // Parent affair number (business_short_number, e.g. "25.081") for detail
  // pages whose entity isn't itself tool-addressable — votes and debates. The
  // chat widget forwards it in the dashboard-state pointer so the assistant can
  // resolve "this vote / this debate" via its affair (which IS addressable),
  // rather than a raw vote/speech id that the tools don't key on.
  affair?: string | null;
}

interface TrailState {
  trail: NavEntry[];
  push: (entry: NavEntry) => void;
  clear: () => void;
  truncateTo: (index: number) => void;
}

const STORAGE_KEY = 'nav_trail';

/**
 * Categorises a path into list-vs-detail so the "same page type → replace"
 * collapse only fires for genuine sibling browsing (e.g. /bills/123 →
 * /bills/456), not for list → detail drill-downs (e.g. /votes → /votes/123,
 * where the list page is the *parent* and must stay in the trail).
 *
 *   /votes      → 'votes/list'
 *   /votes/123  → 'votes/detail'
 *   /votes/123  → /votes/456  → still both 'votes/detail' → collapse (intended)
 */
function pageType(path: string): string {
  const noQuery = path.split('?')[0];
  const m = noQuery.match(/^\/([a-z-]+)(?:\/(.+))?$/);
  if (!m) return noQuery;
  return m[2] ? `${m[1]}/detail` : `${m[1]}/list`;
}

function loadTrail(): NavEntry[] {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    return raw ? (JSON.parse(raw) as NavEntry[]) : [];
  } catch {
    return [];
  }
}

function saveTrail(trail: NavEntry[]) {
  try {
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(trail));
  } catch {
    // sessionStorage unavailable — silently skip
  }
}

export const useTrailStore = create<TrailState>((set, get) => ({
  trail: loadTrail(),

  push: (entry: NavEntry) => {
    const trail = get().trail;
    const entryType   = pageType(entry.path);
    const isListPage  = entryType.endsWith('/list');

    // List-page pushes are "fresh entry points" / domain roots: reset the
    // trail to just this entry. This drops foreign-domain crumbs (e.g. a
    // stale /members/<id> before /votes) AND any same-domain crumbs — most
    // importantly a detail page you drilled into and then navigated back up
    // from. Keeping same-domain detail crumbs here inverted/duplicated the
    // breadcrumb: because the list path now carries the filter query string,
    // navigating from an affair detail to the (differently-filtered) list no
    // longer matched the stored list entry, producing "Bills > 24.096 > Bills".
    // A list page must never be a child of its own detail; it is always the
    // root that subsequent detail drill-downs hang off. Detail-page pushes
    // (below) keep trusting the existing trail so the drill chain builds up.
    if (isListPage) {
      const last = trail[trail.length - 1];
      // Re-render guard: identical re-push (same filters/label) → no-op. List
      // pages legitimately re-push with updated filters (e.g. changing a Votes
      // filter or analytics mode); those must go through so the crumb refreshes.
      if (trail.length === 1 && last
          && last.path    === entry.path
          && last.label   === entry.label
          && last.filters === entry.filters) {
        return;
      }
      const next = [entry];
      saveTrail(next);
      set({ trail: next });
      return;
    }

    // ── Detail-page pushes ──────────────────────────────────────────────────
    const working = trail;

    // Re-render guard: skip only when the new entry is *exactly* the same as
    // the last one (path + label + filters).
    const last = working[working.length - 1];
    if (last
        && last.path    === entry.path
        && last.label   === entry.label
        && last.filters === entry.filters) {
      return;
    }
    // Path exists earlier in trail — replace with the fresh entry (filters
    // string may have changed) and truncate anything deeper. This handles
    // both "navigating backward to a parent" and "list-page filter update"
    // cases uniformly.
    const existingIdx = working.findIndex(e => e.path === entry.path);
    if (existingIdx !== -1) {
      const next = [...working.slice(0, existingIdx), entry];
      saveTrail(next);
      set({ trail: next });
      return;
    }
    // Last entry is the same page type (e.g. /bills/123 → /bills/456) —
    // user is browsing siblings at the same level, replace rather than append
    if (working.length > 0 && pageType(working[working.length - 1].path) === entryType) {
      const next = [...working.slice(0, -1), entry];
      saveTrail(next);
      set({ trail: next });
      return;
    }
    // Genuine deeper drill-down — append
    const next = [...working, entry];
    saveTrail(next);
    set({ trail: next });
  },

  clear: () => {
    saveTrail([]);
    set({ trail: [] });
  },

  truncateTo: (index: number) => {
    const trail = get().trail;
    const next = trail.slice(0, index + 1);
    saveTrail(next);
    set({ trail: next });
  },
}));
